import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton, BookCTA } from "@/components/marketing";
import { GUIDES } from "@/data/guides";
import { ARTICLES } from "@/data/articles";

const TEAL = "#22D3C5";
const EASE = [0.22, 1, 0.36, 1];

export default function Inspiration() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const featured = ARTICLES[0];
  const rest = ARTICLES.slice(1);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden" data-testid="inspiration-page">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-14">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
              <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Inspiration</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
            className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance max-w-3xl">
            Viden, der gør jer <span style={{ color: TEAL }}>skarpere</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
            className="text-muted-foreground mt-6 text-base sm:text-lg max-w-2xl leading-relaxed">
            Konkret viden om digital kommunikation - gratis og lige til at gå til. Hent vores guides og læs skarpe tanker om brand, synlighed og AI.
          </motion.p>
        </div>
      </section>

      {/* Guides */}
      <section className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8 pb-8">
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-6">Guides</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {GUIDES.map((g, i) => (
            <motion.div key={g.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.08, ease: EASE }}
              className="group rounded-2xl border border-border bg-card overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25"
              style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}
              data-testid={`guide-card-${g.slug}`}>
              <div className="aspect-[16/10] overflow-hidden bg-secondary">
                <img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">{g.eyebrow}</p>
                <h2 className="font-display text-xl font-medium leading-snug">{g.title}</h2>
                <p className="text-[13.5px] text-muted-foreground mt-3 leading-relaxed flex-1">{g.short}</p>
                <Link to={`/inspiration/${g.slug}`} data-testid={`guide-cta-${g.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-foreground">
                  <Download className="h-4 w-4" /> Hent guiden gratis
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Annonceberegner */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0.75, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4, ease: EASE }}>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Gratis værktøj</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Find det rigtige annoncebudget</h2>
            <p className="text-muted-foreground mt-5 leading-relaxed max-w-xl">
              De fleste kampagner rammer ved siden af på grund af to klassiske fejl: Enten ser målgruppen jeres annoncer for få gange, så folk ikke når at huske brandet, eller også rammer I målgruppen for ofte, hvor de ekstra visninger alligevel ikke flytter noget og burde være brugt på nye potentielle kunder.
            </p>
            <div className="mt-8">
              <MButton to="/beregner" size="lg" data-testid="inspiration-calculator-btn">Prøv beregneren <ArrowRight className="h-4 w-4" /></MButton>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0.75, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="rounded-2xl border border-border bg-card p-6 sm:p-8" style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03), 0 24px 50px -34px rgba(17,17,17,0.18)" }}>
            <div className="grid grid-cols-2 gap-3">
              {[["Budget", "6.400", "kr./md."], ["Reach", "20.000", "personer"]].map(([label, value, unit]) => (
                <div key={label} className="rounded-xl border border-border bg-secondary/60 p-5">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">{label}</p>
                  <p className="font-display text-2xl font-medium mt-2" style={{ color: TEAL }}>{value}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{unit}</p>
                </div>
              ))}
            </div>
            <p className="text-[13.5px] text-muted-foreground mt-6 leading-relaxed">
              Beregn på sekunder, hvad det koster at nå jeres målgruppe med den rette frekvens - eller hvor mange I kan nå med jeres budget.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Artikler */}
      <section className="relative z-10 border-t border-border bg-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24">
          <div className="mb-10 max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Artikler</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Skarpe tanker om kommunikation</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">Korte, ærlige indlæg om brand, synlighed og AI - lige til at læse på et par minutter.</p>
          </div>

          {/* Featured article */}
          {featured && (
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, ease: EASE }} className="mb-4">
              <Link to={`/artikler/${featured.slug}`} data-testid={`article-card-${featured.slug}`}
                className="group relative block rounded-2xl border border-border bg-card p-8 sm:p-10 transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25"
                style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
                <span className="absolute left-0 top-0 h-full w-[3px] rounded-l-2xl" style={{ backgroundColor: TEAL }} />
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
                  <span style={{ color: TEAL }}>{featured.eyebrow}</span>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                  <span>{featured.readTime}</span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight leading-tight max-w-2xl">{featured.title}</h3>
                <p className="text-[15px] text-muted-foreground mt-4 leading-relaxed max-w-2xl">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground">
                  Læs artiklen <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" style={{ color: TEAL }} />
                </span>
              </Link>
            </motion.div>
          )}

          {/* Article grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {rest.map((a, i) => (
              <motion.div key={a.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.06, ease: EASE }}>
                <Link to={`/artikler/${a.slug}`} data-testid={`article-card-${a.slug}`}
                  className="group flex flex-col h-full rounded-2xl border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25"
                  style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
                    <span style={{ color: TEAL }}>{a.eyebrow}</span>
                    <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                    <span>{a.readTime}</span>
                  </div>
                  <h3 className="font-display text-xl font-medium leading-snug">{a.title}</h3>
                  <p className="text-[13.5px] text-muted-foreground mt-3 leading-relaxed flex-1">{a.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                    Læs artiklen <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
